/**
 * Fetal Morph System - Main Entry Point
 * 
 * Combines all morph system components into a unified API for the 3D viewer.
 * 
 * Usage:
 *   const morphSystem = new FetalMorphSystem();
 *   morphSystem.initialize(geometry);
 *   morphSystem.updateFromBiometrics(measurements);
 *   morphSystem.applyToGeometry(positions);
 */

import { BiometricNormalizer, biometricNormalizer } from './BiometricNormalizer';
import { BiometricToMorphMapper, biometricToMorphMapper } from './BiometricToMorphMapper';
import { MorphDeformationEngine, createMorphDeformationEngine } from './MorphDeformationEngine';
import { MORPH_CHANNELS, getAllChannelIds } from './MorphChannelDefinitions';

/**
 * FetalMorphSystem class
 * 
 * High-level API that orchestrates the entire morph deformation pipeline.
 */
export class FetalMorphSystem {
  constructor() {
    this.normalizer = biometricNormalizer;
    this.mapper = biometricToMorphMapper;
    this.engine = createMorphDeformationEngine();
    
    this.isInitialized = false;
    this.currentMorphValues = {};
    this.lastMeasurements = null;
    
    // Initialize morph values to 0
    for (const channelId of getAllChannelIds()) {
      this.currentMorphValues[channelId] = 0;
    }
  }

  /**
   * Initialize the morph system with mesh geometry
   * 
   * @param {THREE.BufferGeometry} geometry - The fetal head mesh geometry
   * @returns {boolean} Success status
   */
  initialize(geometry) {
    if (!geometry) {
      console.error('FetalMorphSystem: No geometry provided');
      return false;
    }

    const success = this.engine.initialize(geometry);
    this.isInitialized = success;
    
    if (success) {
      console.log('FetalMorphSystem initialized successfully');
    }
    
    return success;
  }

  /**
   * Update morph values from raw biometric measurements
   * 
   * @param {Object} measurements - Raw biometric measurements from UI sliders
   * @returns {Object} Current morph channel values
   */
  updateFromBiometrics(measurements) {
    if (!measurements) return this.currentMorphValues;

    // Step 1: Normalize biometric inputs
    const normalized = this.normalizer.normalizeAll(measurements);
    
    // Step 2: Map to morph channels
    this.currentMorphValues = this.mapper.mapToMorphValues(normalized);
    
    // Store for reference
    this.lastMeasurements = { ...measurements };
    
    return this.currentMorphValues;
  }

  /**
   * Apply current morph values to geometry
   * 
   * @param {THREE.BufferAttribute} positions - The position attribute to modify
   * @returns {boolean} Success status
   */
  applyToGeometry(positions) {
    if (!this.isInitialized) {
      console.warn('FetalMorphSystem: Not initialized');
      return false;
    }

    return this.engine.applyDeformation(positions, this.currentMorphValues);
  }

  /**
   * Combined update and apply in one call
   * 
   * @param {Object} measurements - Raw biometric measurements
   * @param {THREE.BufferAttribute} positions - Position attribute to modify
   * @returns {boolean} Success status
   */
  updateAndApply(measurements, positions) {
    this.updateFromBiometrics(measurements);
    return this.applyToGeometry(positions);
  }

  /**
   * Check if measurements have changed enough to warrant update
   * 
   * @param {Object} newMeasurements - New measurements to compare
   * @returns {boolean} True if significant change detected
   */
  hasSignificantChange(newMeasurements) {
    if (!this.lastMeasurements) return true;

    for (const [key, value] of Object.entries(newMeasurements)) {
      const lastValue = this.lastMeasurements[key];
      if (lastValue === undefined) return true;
      
      // Consider > 0.5% change as significant
      const diff = Math.abs(value - lastValue);
      const threshold = Math.abs(lastValue) * 0.005 + 0.01;
      
      if (diff > threshold) return true;
    }

    return false;
  }

  /**
   * Reset geometry to original positions
   * 
   * @param {THREE.BufferAttribute} positions - Position attribute to reset
   */
  reset(positions) {
    if (!this.isInitialized) return;
    
    this.engine.reset(positions);
    
    // Reset morph values
    for (const channelId of getAllChannelIds()) {
      this.currentMorphValues[channelId] = 0;
    }
    
    this.lastMeasurements = null;
  }

  /**
   * Get current morph channel values
   */
  getMorphValues() {
    return { ...this.currentMorphValues };
  }

  /**
   * Get detailed state for debugging/saving
   */
  getState() {
    return {
      isInitialized: this.isInitialized,
      lastMeasurements: this.lastMeasurements ? { ...this.lastMeasurements } : null,
      morphValues: { ...this.currentMorphValues },
      normalizedBiometrics: this.lastMeasurements 
        ? this.normalizer.normalizeAll(this.lastMeasurements) 
        : null
    };
  }

  /**
   * Load state (for restoring saved editor state)
   */
  loadState(state) {
    if (state.morphValues) {
      this.currentMorphValues = { ...state.morphValues };
    }
    if (state.lastMeasurements) {
      this.lastMeasurements = { ...state.lastMeasurements };
    }
  }

  /**
   * Clean up resources
   */
  dispose() {
    this.engine.dispose();
    this.isInitialized = false;
    this.currentMorphValues = {};
    this.lastMeasurements = null;
  }
}

// Export factory function
export const createFetalMorphSystem = () => new FetalMorphSystem();

// Export all sub-modules for direct access if needed
export { 
  BiometricNormalizer, 
  biometricNormalizer,
  BiometricToMorphMapper, 
  biometricToMorphMapper,
  MorphDeformationEngine,
  createMorphDeformationEngine,
  MORPH_CHANNELS,
  getAllChannelIds
};

export default FetalMorphSystem;
